import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';

/**
 * JourneyStepper — the single, unified 5-stage progress indicator
 * shared across every page of the customer's quote → proof → order
 * journey. Replaces the previous segmented duo (`QuoteProgressSteps`
 * + `OrderStepper`) which made the customer feel they were stuck in a
 * long form ("I'm still not done yet?").
 *
 * Visible stages (ordered by emotional commitment, not by route order):
 *
 *   1. Configure Product   — `/quote` (all 6 internal wizard steps)
 *   2. Upload Artwork      — `/upload-artwork`
 *   3. Review & Timeline   — `/order-summary`
 *   4. Checkout            — `/customer-info` + `/review-order`
 *   5. Approve Proof       — `/proof-approval` + `/order-confirmation`
 *
 * Stage 5 is intentionally last + post-checkout — per the customer's
 * UX brief: proof approval should NOT interrupt checkout. Customers
 * commit first; perfectionism comes second.
 *
 * Visual language: same arrow-tag clip-path style the previous
 * QuoteCalculator stepper used (operator approved it in iter114s.36),
 * just extended to 5 stages. Active stage = lighter EG3 green fill +
 * white text + scale-up; completed stages = soft tint + green check
 * + clickable to jump back; future stages = neutral gray.
 *
 * Routes per stage are configurable via `STAGE_ROUTES` so a page can
 * mark a stage as "completed" but route-disabled (e.g. Approve Proof
 * is only reachable via a backend-driven redirect once a proof is
 * generated).
 */
const STAGES = [
  {
    n: 1,
    label: 'Configure Product',
    sublabel: 'Pick category, size & material',
    route: '/quote',
  },
  {
    n: 2,
    label: 'Upload Artwork',
    sublabel: 'Drop your design',
    route: '/upload-artwork',
  },
  {
    n: 3,
    label: 'Review & Timeline',
    sublabel: 'Confirm details + see what\'s next',
    route: '/order-summary',
  },
  {
    n: 4,
    label: 'Checkout',
    sublabel: 'Address & payment',
    route: '/customer-info',
  },
  {
    n: 5,
    label: 'Approve Proof',
    sublabel: 'Sign off + ship',
    route: null, // backend-gated — reachable only after order submission
  },
];

export const JourneyStepper = ({ currentStage = 1, orderId = null }) => {
  const navigate = useNavigate();

  const handleClick = (stage) => {
    // Only clickable when:
    //   1. The stage has a route AND
    //   2. The stage is "behind" the customer (already completed, can
    //      jump back to fix something).
    if (!stage.route) return;
    if (stage.n >= currentStage) return;
    // Stages 2-4 typically need an orderId on the URL state. The pages
    // themselves handle the missing-order case gracefully (redirect
    // back to /quote or /dashboard), so we just navigate without
    // forcing the orderId here.
    if (stage.n === 1) {
      navigate(stage.route);
    } else if (orderId) {
      navigate(stage.route, { state: { orderId } });
    } else {
      navigate(stage.route);
    }
  };

  return (
    <div
      className="w-full bg-white border-b border-[#E5E7EB]"
      data-testid="journey-stepper"
    >
      <div className="max-w-6xl mx-auto px-4 py-5">
        <div className="flex items-stretch justify-center gap-1 flex-wrap" data-testid="journey-stepper-row">
          {STAGES.map((stage, idx) => {
            const isComplete = stage.n < currentStage;
            const isActive = stage.n === currentStage;
            const isClickable = isComplete && !!stage.route;
            const isLast = idx === STAGES.length - 1;

            // Arrow-tag fill colors — matches the previous
            // QuoteProgressSteps style language so frequent users
            // don't have to relearn the chrome.
            const tagBg = isActive
              ? 'bg-[#6FBF73] text-white'
              : isComplete
                ? 'bg-[#E9F2EA] text-[#2F5D3A]'
                : 'bg-[#F4F7F2] text-[#9DB5A4]';

            const tagHover = isClickable ? 'hover:bg-[#D9EADC] cursor-pointer' : '';

            const circleBg = isActive
              ? 'bg-white text-[#2F5D3A]'
              : isComplete
                ? 'bg-[#6FBF73] text-white'
                : 'bg-white text-[#9DB5A4] border border-[#C4D4C9]';

            const titleHint = (() => {
              if (isActive) return `${stage.label} (current step)`;
              if (isClickable) return `Click to jump back to ${stage.label}`;
              if (isComplete) return `${stage.label} (complete)`;
              return `${stage.label} (locked — finish the previous steps first)`;
            })();

            return (
              <button
                key={stage.n}
                type="button"
                disabled={!isClickable}
                onClick={() => handleClick(stage)}
                title={titleHint}
                aria-label={titleHint}
                aria-current={isActive ? 'step' : undefined}
                data-testid={`journey-stage-${stage.n}`}
                className={`relative flex items-center gap-2 pl-3 pr-5 py-2.5 rounded-l-lg text-left ${tagBg} ${tagHover} transition-colors focus:outline-none focus:ring-2 focus:ring-[#6FBF73] focus:ring-offset-2`}
                style={{
                  clipPath: !isLast
                    ? 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 0 50%)'
                    : 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 50%)',
                }}
              >
                <span
                  className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${circleBg}`}
                  aria-hidden="true"
                >
                  {isComplete ? <Check className="w-3.5 h-3.5" /> : stage.n}
                </span>
                <span className="flex flex-col items-start min-w-0">
                  <span className={`text-xs sm:text-sm font-semibold leading-tight whitespace-nowrap ${isActive ? 'text-white' : ''}`}>
                    {stage.label}
                  </span>
                  <span
                    className={`text-[9px] sm:text-[10px] uppercase tracking-wide leading-tight mt-0.5 hidden xl:block whitespace-nowrap ${
                      isActive ? 'text-white/80' : isComplete ? 'text-[#4A7C59]/70' : 'text-[#9DB5A4]'
                    }`}
                  >
                    {stage.sublabel}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default JourneyStepper;

/**
 * QuoteWizardProgress — 3-page sub-tracker used INSIDE the
 * QuoteCalculator. Mirrors the chevron-tag visual language of the
 * outer `JourneyStepper` so the customer doesn't context-switch, but
 * shows the wizard's actual internal page progression (Product →
 * Size & Quantity → Shipping) instead of the broader 5-stage journey.
 *
 * iter114s.59 — Replaces the prior `<JourneyStepper currentStage={1} />`
 * usage in QuoteCalculator, which left "Configure Product" stuck-green
 * even when the customer was on PAGE 3 · Shipping (the wizard's
 * internal step 6 still maps to /quote in the outer journey, but the
 * customer reasonably expects the visual to update as they move
 * through the wizard's three pages).
 *
 * `step` is the QuoteCalculator's internal 1-6 step number. We map it
 * to the 3 user-visible pages here so callers can keep passing the
 * raw wizard step without doing the math themselves.
 */
const WIZARD_PAGES = [
  { n: 1, label: 'Product',         sublabel: 'Category, format & material' },
  { n: 2, label: 'Size & Quantity', sublabel: 'Dimensions & volume' },
  { n: 3, label: 'Shipping',        sublabel: 'Method & final quote' },
];

const stepToPage = (step) => {
  if (step >= 6) return 3;
  if (step >= 4) return 2;
  return 1;
};

export const QuoteWizardProgress = ({ step = 1, onJump }) => {
  const currentPage = stepToPage(step);

  return (
    <div className="w-full bg-white border-b border-[#E5E7EB]" data-testid="quote-wizard-progress">
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex items-stretch justify-center gap-1 flex-wrap">
          {WIZARD_PAGES.map((page, idx) => {
            const isComplete = page.n < currentPage;
            const isActive = page.n === currentPage;
            const isClickable = isComplete && typeof onJump === 'function';
            const isLast = idx === WIZARD_PAGES.length - 1;

            const tagBg = isActive
              ? 'bg-[#6FBF73] text-white'
              : isComplete
                ? 'bg-[#E9F2EA] text-[#2F5D3A]'
                : 'bg-[#F4F7F2] text-[#9DB5A4]';
            const tagHover = isClickable ? 'hover:bg-[#D9EADC] cursor-pointer' : '';
            const circleBg = isActive
              ? 'bg-white text-[#2F5D3A]'
              : isComplete
                ? 'bg-[#6FBF73] text-white'
                : 'bg-white text-[#9DB5A4] border border-[#C4D4C9]';

            return (
              <button
                key={page.n}
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onJump(page.n)}
                aria-current={isActive ? 'step' : undefined}
                data-testid={`wizard-progress-page-${page.n}`}
                className={`relative flex items-center gap-2 pl-3 pr-5 py-2 rounded-l-lg text-left ${tagBg} ${tagHover} transition-colors focus:outline-none focus:ring-2 focus:ring-[#6FBF73] focus:ring-offset-2`}
                style={{
                  clipPath: !isLast
                    ? 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 0 50%)'
                    : 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 50%)',
                }}
              >
                <span
                  className={`flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full font-bold text-xs ${circleBg}`}
                  aria-hidden="true"
                >
                  {isComplete ? <Check className="w-3.5 h-3.5" /> : page.n}
                </span>
                <span className="flex flex-col items-start min-w-0">
                  <span className={`text-xs sm:text-sm font-semibold leading-tight whitespace-nowrap ${isActive ? 'text-white' : ''}`}>
                    {`PAGE ${page.n} · ${page.label}`}
                  </span>
                  <span
                    className={`text-[9px] sm:text-[10px] uppercase tracking-wide leading-tight mt-0.5 hidden lg:block whitespace-nowrap ${
                      isActive ? 'text-white/80' : isComplete ? 'text-[#4A7C59]/70' : 'text-[#9DB5A4]'
                    }`}
                  >
                    {page.sublabel}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
